import React, { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import {
  tenantApi,
  type ExchangeSenderTestOperation,
  type ExchangeMailboxRegistration,
  type ExchangeSenderRegistration,
} from '../../services/tenantApi';
import './ExchangeEmailSendersManagement.css';

const ELIGIBILITY_LABELS: Record<string, string> = {
  CURRENT_EVIDENCE_REQUIRED: 'Current evidence required',
  TRUSTED_EVIDENCE_REQUIRED: 'Trusted evidence required',
  OPERATOR_PREREQUISITES_REQUIRED: 'Operator prerequisites required',
  EXACT_ALIAS_CONFIRMATION_REQUIRED: 'Exact alias confirmation required',
  SENDER_OR_CONNECTION_DISABLED: 'Sender or mailbox is disabled',
  TRUSTED_CONNECTION_REQUIRED: 'Trusted connection unavailable',
  TENANT_OWNERSHIP_REQUIRED: 'Tenant ownership is not verified',
  INVALID_FROM_ADDRESS: 'Invalid sender address',
};

const TEST_TERMINAL_STATES = new Set(['Accepted', 'Rejected', 'Unknown', 'LocalCaptured', 'DeadLettered']);
const TEST_FAILURE_STATES = new Set(['Rejected', 'Unknown', 'DeadLettered']);

const testOutcomeMessage = (operation: ExchangeSenderTestOperation): string => {
  if (operation.state === 'Accepted') return 'Microsoft Graph accepted the message. This does not confirm delivery. Check the recipient inbox and confirm the received alias.';
  if (operation.state === 'LocalCaptured') return 'The message was captured locally. No external email was sent.';
  if (operation.state === 'Unknown') return 'The delivery outcome is unknown. Review the provider trace before submitting another test; the message may have been accepted.';
  if (operation.state === 'DeadLettered') return 'The test could not be published to the delivery queue. Ask an administrator to check the publisher and Service Bus configuration.';
  if (operation.state !== 'Rejected') return 'The test is awaiting processing. No provider acceptance has been recorded.';
  const code = operation.errorCode;
  if (code === 'ErrorSendAsDenied') return 'Exchange denied sending as this address. Check the application mailbox access restrictions and send-address configuration.';
  if (operation.providerStatusCode === 403 || ['403', 'GRAPH_ACCESS_DENIED', 'ErrorAccessDenied', 'Authorization_RequestDenied'].includes(code || ''))
    return 'Microsoft Graph denied this send. Check application Mail.Send admin consent and Exchange mailbox access restrictions. The message was not accepted.';
  if (operation.providerStatusCode === 401 || ['401', 'GRAPH_AUTHENTICATION_FAILED', 'InvalidAuthenticationToken'].includes(code || ''))
    return 'Microsoft Graph rejected authentication. Ask an administrator to check the application tenant, credential and token permissions.';
  if (operation.providerStatusCode === 404 || ['GRAPH_MAILBOX_NOT_FOUND', 'ResourceNotFound', 'MailboxNotEnabledForRESTAPI'].includes(code || ''))
    return 'The Exchange mailbox is missing or unavailable to Microsoft Graph. Check the registered mailbox object ID and Exchange Online mailbox status.';
  if (operation.providerStatusCode === 429 || ['GRAPH_THROTTLED', 'TooManyRequests'].includes(code || ''))
    return 'Microsoft Graph throttled this request. Review the provider limits before submitting a new test.';
  if (code === 'ErrorQuotaExceeded') return 'The mailbox quota was exceeded. An Exchange administrator must check the mailbox capacity.';
  if (code === 'ErrorInvalidRecipients') return 'Exchange rejected the recipient. Check the exact recipient address before submitting a new test.';
  return 'The test was rejected. Review the error code and correlation ID in delivery diagnostics before submitting another test.';
};

export const ExchangeEmailSendersManagement: React.FC = () => {
  useDateTimePreferences();
  const [mailboxes, setMailboxes] = useState<ExchangeMailboxRegistration[]>([]);
  const [senders, setSenders] = useState<ExchangeSenderRegistration[]>([]);
  const [selectedMailboxId, setSelectedMailboxId] = useState<string | null>(null);
  const selectedMailboxRef = useRef<string | null>(null);
  const senderLoadVersion = useRef(0);
  const [loading, setLoading] = useState(true);
  const [loadingSenders, setLoadingSenders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showMailboxForm, setShowMailboxForm] = useState(false);
  const [mailboxName, setMailboxName] = useState('');
  const [mailboxObjectId, setMailboxObjectId] = useState('');
  const [senderLabel, setSenderLabel] = useState('');
  const [senderAddress, setSenderAddress] = useState('');
  const [senderDisplayName, setSenderDisplayName] = useState('');
  const [editingMailbox, setEditingMailbox] = useState<ExchangeMailboxRegistration | null>(null);
  const [editingSender, setEditingSender] = useState<ExchangeSenderRegistration | null>(null);
  const [editName, setEditName] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [evidenceSender, setEvidenceSender] = useState<ExchangeSenderRegistration | null>(null);
  const [evidenceChecks, setEvidenceChecks] = useState({ sharedMailbox: false, addressMembership: false, applicationSend: false, aliasPrerequisites: false });
  const [testSender, setTestSender] = useState<ExchangeSenderRegistration | null>(null);
  const [testRecipient, setTestRecipient] = useState('');
  const [confirmTestRecipient, setConfirmTestRecipient] = useState(false);
  const [testIdempotencyKey, setTestIdempotencyKey] = useState('');
  const [testOperation, setTestOperation] = useState<ExchangeSenderTestOperation | null>(null);
  const [testSubmitting, setTestSubmitting] = useState(false);
  const [aliasSender, setAliasSender] = useState<ExchangeSenderRegistration | null>(null);
  const [aliasOperation, setAliasOperation] = useState<ExchangeSenderTestOperation | null>(null);
  const [aliasFrom, setAliasFrom] = useState('');
  const [aliasSenderAddress, setAliasSenderAddress] = useState('');
  const [aliasReplyTo, setAliasReplyTo] = useState('');
  const [confirmReceivedMessage, setConfirmReceivedMessage] = useState(false);
  const [confirmNoOnBehalfOf, setConfirmNoOnBehalfOf] = useState(false);
  const [aliasLoading, setAliasLoading] = useState(false);

  const loadMailboxes = async () => {
    setLoading(true);
    setError(null);
    try {
      setMailboxes(await tenantApi.getExchangeMailboxes());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load mailboxes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadMailboxes();
    return () => {
      selectedMailboxRef.current = null;
      senderLoadVersion.current++;
    };
  }, []);

  const refreshSenders = async (mailboxId: string) => {
    if (selectedMailboxRef.current !== mailboxId) return;
    const version = ++senderLoadVersion.current;
    const isCurrent = () => selectedMailboxRef.current === mailboxId && senderLoadVersion.current === version;
    setLoadingSenders(true);
    setError(null);
    try {
      const records = await tenantApi.getExchangeMailboxSenders(mailboxId);
      if (isCurrent()) setSenders(records);
    } catch (loadError) {
      if (isCurrent()) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load send addresses');
        setSenders([]);
      }
    } finally {
      if (isCurrent()) setLoadingSenders(false);
    }
  };

  const openMailbox = async (mailboxId: string) => {
    const nextMailboxId = selectedMailboxRef.current === mailboxId ? null : mailboxId;
    selectedMailboxRef.current = nextMailboxId;
    senderLoadVersion.current++;
    setSelectedMailboxId(nextMailboxId);
    setSenders([]);
    setEvidenceSender(null);
    setTestSender(null);
    setTestOperation(null);
    setAliasSender(null);
    setAliasOperation(null);
    setSenderLabel('');
    setSenderAddress('');
    setSenderDisplayName('');
    setEditingMailbox(null);
    setEditingSender(null);
    if (nextMailboxId) await refreshSenders(nextMailboxId);
    else setLoadingSenders(false);
  };

  const registerMailbox = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await tenantApi.createExchangeMailbox({ mailboxObjectId: mailboxObjectId.trim(), name: mailboxName.trim() });
      setMailboxName('');
      setMailboxObjectId('');
      setShowMailboxForm(false);
      setNotice('Mailbox registration saved. Exchange permissions and alias behavior are not verified by registration.');
      await loadMailboxes();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to register mailbox');
    } finally {
      setSaving(false);
    }
  };

  const registerSender = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedMailboxId) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await tenantApi.createExchangeMailboxSender(selectedMailboxId, {
        label: senderLabel.trim(),
        fromAddress: senderAddress.trim(),
        displayName: senderDisplayName.trim() || undefined,
      });
      setSenderLabel('');
      setSenderAddress('');
      setSenderDisplayName('');
      setNotice('Send address registered. It remains unavailable for Graph channels until current evidence is recorded.');
      await refreshSenders(selectedMailboxId);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to register send address');
    } finally {
      setSaving(false);
    }
  };

  const saveRegistrationMetadata = async (event: React.FormEvent) => {
    event.preventDefault();
    if ((!editingMailbox && !editingSender) || !editName.trim() || saving) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (editingMailbox) {
        await tenantApi.updateExchangeMailbox(editingMailbox.mailboxRegistrationId, {
          revision: editingMailbox.revision, name: editName.trim(), isEnabled: editingMailbox.isEnabled,
        });
        await loadMailboxes();
      } else if (editingSender) {
        await tenantApi.updateExchangeSender(editingSender.senderIdentityId, {
          revision: editingSender.revision, label: editName.trim(), displayName: editDisplayName.trim() || undefined,
          isEnabled: editingSender.isEnabled,
        });
      }
      if (selectedMailboxRef.current) await refreshSenders(selectedMailboxRef.current);
      setEditingMailbox(null);
      setEditingSender(null);
      setNotice(editingMailbox ? 'Mailbox updated.' : 'Send address updated.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to update registration');
    } finally {
      setSaving(false);
    }
  };

  const toggleMailbox = async (mailbox: ExchangeMailboxRegistration) => {
    setSaving(true);
    setError(null);
    try {
      await tenantApi.updateExchangeMailbox(mailbox.mailboxRegistrationId, {
        revision: mailbox.revision,
        name: mailbox.name,
        isEnabled: !mailbox.isEnabled,
      });
      await loadMailboxes();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to update mailbox');
    } finally {
      setSaving(false);
    }
  };

  const toggleSender = async (sender: ExchangeSenderRegistration) => {
    setSaving(true);
    setError(null);
    try {
      await tenantApi.updateExchangeSender(sender.senderIdentityId, {
        revision: sender.revision,
        label: sender.label,
        displayName: sender.displayName || undefined,
        isEnabled: !sender.isEnabled,
      });
      if (selectedMailboxId) await refreshSenders(selectedMailboxId);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to update send address');
    } finally {
      setSaving(false);
    }
  };

  const recordPrerequisites = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!evidenceSender) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await tenantApi.recordExchangeSenderPrerequisiteEvidence(evidenceSender.senderIdentityId, {
        revision: evidenceSender.revision,
        sharedMailboxAttested: evidenceChecks.sharedMailbox,
        addressMembershipAttested: evidenceChecks.addressMembership,
        applicationMailSendAttested: evidenceChecks.applicationSend,
        aliasPreservationAttested: evidenceChecks.aliasPrerequisites,
      });
      if (selectedMailboxId) await refreshSenders(selectedMailboxId);
      setEvidenceSender(null);
      setEvidenceChecks({ sharedMailbox: false, addressMembership: false, applicationSend: false, aliasPrerequisites: false });
      setNotice('Operator prerequisites recorded. A separate controlled send and recipient-side alias confirmation are still required.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to record prerequisites');
    } finally {
      setSaving(false);
    }
  };

  const startControlledTest = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!testSender || testSender.canStartControlledTest !== true || !testRecipient.trim() || !confirmTestRecipient || testSubmitting || testOperation) return;
    setTestSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const operation = await tenantApi.startExchangeSenderTest(testSender.senderIdentityId, {
        revision: testSender.revision,
        mailboxRevision: testSender.mailboxRevision,
        idempotencyKey: testIdempotencyKey,
        recipient: testRecipient.trim(),
        confirmRecipient: true,
      });
      setTestOperation(operation);
      setNotice(TEST_TERMINAL_STATES.has(operation.state) ? null : 'Test operation queued. No delivery outcome is inferred until the operation completes.');
    } catch (testError) {
      setError(testError instanceof Error ? testError.message : 'Failed to start sender test');
    } finally {
      setTestSubmitting(false);
    }
  };

  const refreshTestOperation = async () => {
    if (!testOperation) return;
    try {
      const updated = await tenantApi.getExchangeSenderTestOperation(testOperation.operationId);
      setTestOperation(current => current?.operationId === updated.operationId ? updated : current);
      if (TEST_TERMINAL_STATES.has(updated.state)) setNotice(null);
      if (selectedMailboxId && updated.state === 'Accepted')
        await refreshSenders(selectedMailboxId);
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : 'Failed to refresh test status');
    }
  };

  useEffect(() => {
    if (!testOperation || TEST_TERMINAL_STATES.has(testOperation.state)) return;
    const operationId = testOperation.operationId;
    const mailboxId = testSender?.mailboxRegistrationId;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const updated = await tenantApi.getExchangeSenderTestOperation(operationId);
        if (cancelled) return;
        if (updated.operationId !== operationId) throw new Error('The test status response did not match the submitted operation.');
        setTestOperation(current => current?.operationId === operationId ? updated : current);
        if (TEST_TERMINAL_STATES.has(updated.state)) {
          setNotice(null);
          if (updated.state === 'Accepted' && mailboxId) await refreshSenders(mailboxId);
          return;
        }
      } catch (statusError) {
        if (cancelled) return;
        setError(statusError instanceof Error ? statusError.message : 'Unable to refresh the test status.');
        return;
      }
      if (!cancelled && ++attempts < 100) timer = setTimeout(() => void poll(), 3000);
      else if (!cancelled) setError('The test is still processing. Automatic status checks have paused; refresh the status before submitting another test.');
    };
    timer = setTimeout(() => void poll(), 3000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [testOperation?.operationId, testOperation?.state, testSender?.mailboxRegistrationId]);

  const openAliasConfirmation = async (sender: ExchangeSenderRegistration) => {
    setAliasSender(sender);
    setAliasOperation(null);
    setAliasFrom('');
    setAliasSenderAddress('');
    setAliasReplyTo('');
    setConfirmReceivedMessage(false);
    setConfirmNoOnBehalfOf(false);
    setAliasLoading(true);
    setError(null);
    try {
      setAliasOperation(await tenantApi.getLatestAcceptedExchangeSenderTest(sender.senderIdentityId, sender.revision));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'No accepted test is available for this sender revision');
    } finally {
      setAliasLoading(false);
    }
  };

  const confirmAlias = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!aliasSender || !aliasOperation || !confirmReceivedMessage || !confirmNoOnBehalfOf || saving) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await tenantApi.confirmExchangeSenderAlias(aliasSender.senderIdentityId, {
        revision: aliasSender.revision,
        testOperationId: aliasOperation.operationId,
        observedFromAddress: aliasFrom.trim(),
        observedSenderAddress: aliasSenderAddress.trim(),
        observedReplyToAddress: aliasReplyTo.trim(),
        confirmReceivedMessage: true,
        noOnBehalfOfConfirmed: true,
      });
      if (selectedMailboxId) await refreshSenders(selectedMailboxId);
      setAliasSender(null);
      setAliasOperation(null);
      setNotice('Recipient-observed alias confirmation recorded for this sender revision.');
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : 'Failed to confirm alias behavior');
    } finally {
      setSaving(false);
    }
  };

  const retireMailbox = async (mailbox: ExchangeMailboxRegistration) => {
    if (!window.confirm(`Retire ${mailbox.name}? Active sender registrations must be retired first.`)) return;
    setSaving(true);
    setError(null);
    try {
      await tenantApi.retireExchangeMailbox(mailbox.mailboxRegistrationId, mailbox.revision);
      if (selectedMailboxRef.current === mailbox.mailboxRegistrationId) {
        selectedMailboxRef.current = null;
        senderLoadVersion.current++;
        setSelectedMailboxId(null);
        setSenders([]);
      }
      await loadMailboxes();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to retire mailbox');
    } finally {
      setSaving(false);
    }
  };

  const retireSender = async (sender: ExchangeSenderRegistration) => {
    if (!window.confirm(`Retire ${sender.label} <${sender.fromAddress}>? Enabled channel dependencies will block retirement.`)) return;
    setSaving(true);
    setError(null);
    try {
      await tenantApi.retireExchangeSender(sender.senderIdentityId, sender.revision);
      if (selectedMailboxId) await refreshSenders(selectedMailboxId);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to retire send address');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="management-section exchange-senders-view">
      <div className="section-header">
        <div>
          <h2>Email Senders</h2>
        </div>
        <button className="btn-primary" type="button" onClick={() => setShowMailboxForm((shown) => !shown)}>
          {showMailboxForm ? 'Cancel' : '+ Register mailbox'}
        </button>
      </div>

      {error && <div className="error-message" role="alert">{error}</div>}
      {notice && <div className="success-message" role="status">{notice}</div>}

      {(editingMailbox || editingSender) && (
        <form className="exchange-sender-form" aria-label={editingMailbox ? 'Edit mailbox' : 'Edit send address'}
          onSubmit={(event) => void saveRegistrationMetadata(event)}>
          <h3>{editingMailbox ? 'Edit mailbox' : 'Edit send address'}</h3>
          <div className="exchange-mailbox-id">{editingMailbox?.mailboxObjectId || editingSender?.fromAddress}</div>
          <label>
            {editingMailbox ? 'Mailbox name' : 'Label'}
            <input name="registrationName" value={editName} onChange={(event) => setEditName(event.target.value)}
              maxLength={200} required disabled={saving} autoFocus />
          </label>
          {editingSender && <label>
            Display name
            <input name="registrationDisplayName" value={editDisplayName} onChange={(event) => setEditDisplayName(event.target.value)}
              maxLength={200} disabled={saving} />
          </label>}
          <div className="form-actions">
            <button className="btn-secondary" type="button" disabled={saving} onClick={() => { setEditingMailbox(null); setEditingSender(null); }}>Cancel</button>
            <button className="btn-primary" type="submit" disabled={saving || !editName.trim()}>{saving ? 'Saving...' : 'Save changes'}</button>
          </div>
        </form>
      )}

      {showMailboxForm && (
        <form className="exchange-sender-form" onSubmit={(event) => void registerMailbox(event)}>
          <h3>Register mailbox</h3>
          <label>
            Mailbox name
            <input value={mailboxName} onChange={(event) => setMailboxName(event.target.value)} maxLength={200} required />
          </label>
          <label>
            Mailbox object ID
            <input value={mailboxObjectId} onChange={(event) => setMailboxObjectId(event.target.value)} maxLength={36} required />
          </label>
          <div className="form-actions">
            <button className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Register mailbox'}</button>
          </div>
        </form>
      )}

      {loading ? <div className="loading">Loading mailboxes...</div> : (
        <div className="exchange-mailbox-list">
          {mailboxes.map((mailbox) => {
            const expanded = selectedMailboxId === mailbox.mailboxRegistrationId;
            return (
              <article className="exchange-mailbox-row" key={mailbox.mailboxRegistrationId}>
                <div className="exchange-mailbox-summary">
                  <div>
                    <h3>{mailbox.name}</h3>
                    <div className="exchange-mailbox-id">{mailbox.mailboxObjectId}</div>
                    <span className={`status-badge ${mailbox.isEnabled && !mailbox.isRetired ? 'enabled' : 'disabled'}`}>
                      {mailbox.isRetired ? 'Retired' : mailbox.isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="exchange-sender-actions">
                    {!mailbox.isRetired && <button className="btn-secondary" type="button" disabled={saving}
                      aria-label={`Edit mailbox ${mailbox.name}`} title="Edit mailbox"
                      onClick={() => { setEditingMailbox(mailbox); setEditingSender(null); setEditName(mailbox.name); setError(null); setNotice(null); }}>
                      <Pencil size={16} aria-hidden="true" />
                    </button>}
                    <button className="btn-secondary" type="button" onClick={() => void openMailbox(mailbox.mailboxRegistrationId)}>
                      {expanded ? 'Hide send addresses' : 'Manage send addresses'}
                    </button>
                    {!mailbox.isRetired && <button className="btn-secondary" type="button" disabled={saving} onClick={() => void toggleMailbox(mailbox)}>
                      {mailbox.isEnabled ? 'Disable' : 'Enable'}
                    </button>}
                    {!mailbox.isRetired && <button className="btn-danger" type="button" disabled={saving} onClick={() => void retireMailbox(mailbox)}>Retire</button>}
                  </div>
                </div>

                {expanded && (
                  <div className="exchange-senders-panel">
                    <div className="exchange-senders-heading">
                      <h4>Send addresses</h4>
                      <span>{mailbox.name}</span>
                    </div>
                    {mailbox.isEnabled && !mailbox.isRetired && (
                      <form className="exchange-sender-form compact" onSubmit={(event) => void registerSender(event)}>
                        <label>
                          Label
                          <input value={senderLabel} onChange={(event) => setSenderLabel(event.target.value)} maxLength={200} required />
                        </label>
                        <label>
                          Existing SMTP address
                          <input type="email" value={senderAddress} onChange={(event) => setSenderAddress(event.target.value)} maxLength={320} required />
                        </label>
                        <label>
                          Display name
                          <input value={senderDisplayName} onChange={(event) => setSenderDisplayName(event.target.value)} maxLength={200} />
                        </label>
                        <button className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Register address'}</button>
                      </form>
                    )}
                    {evidenceSender?.mailboxRegistrationId === mailbox.mailboxRegistrationId && (
                      <form className="exchange-sender-form evidence-form" onSubmit={(event) => void recordPrerequisites(event)}>
                        <h4>Operator prerequisites: {evidenceSender.label} &lt;{evidenceSender.fromAddress}&gt;</h4>
                        <p className="help-text">Recording these statements does not send email or confirm how the alias appears to recipients.</p>
                        <label className="evidence-check">
                          <input type="checkbox" checked={evidenceChecks.sharedMailbox} onChange={(event) => setEvidenceChecks({ ...evidenceChecks, sharedMailbox: event.target.checked })} />
                          The registered mailbox is an existing shared mailbox.
                        </label>
                        <label className="evidence-check">
                          <input type="checkbox" checked={evidenceChecks.addressMembership} onChange={(event) => setEvidenceChecks({ ...evidenceChecks, addressMembership: event.target.checked })} />
                          The registered send address exists on this mailbox.
                        </label>
                        <label className="evidence-check">
                          <input type="checkbox" checked={evidenceChecks.applicationSend} onChange={(event) => setEvidenceChecks({ ...evidenceChecks, applicationSend: event.target.checked })} />
                          The existing application has Send As permission for this mailbox.
                        </label>
                        <label className="evidence-check">
                          <input type="checkbox" checked={evidenceChecks.aliasPrerequisites} onChange={(event) => setEvidenceChecks({ ...evidenceChecks, aliasPrerequisites: event.target.checked })} />
                          Exchange alias-sending prerequisites have been checked by an operator.
                        </label>
                        <div className="form-actions">
                          <button className="btn-secondary" type="button" disabled={saving} onClick={() => setEvidenceSender(null)}>Cancel</button>
                          <button className="btn-primary" disabled={saving || Object.values(evidenceChecks).some(value => !value)}>
                            {saving ? 'Recording...' : 'Record prerequisites'}
                          </button>
                        </div>
                      </form>
                    )}
                    {testSender?.mailboxRegistrationId === mailbox.mailboxRegistrationId && (
                      <form className="exchange-sender-form evidence-form" onSubmit={(event) => void startControlledTest(event)}>
                        <h4>Controlled test: {testSender.label} &lt;{testSender.fromAddress}&gt;</h4>
                        <label>
                          Confirmed test recipient
                          <input type="email" value={testRecipient} onChange={(event) => setTestRecipient(event.target.value)} required />
                        </label>
                        <label className="evidence-check">
                          <input type="checkbox" checked={confirmTestRecipient} onChange={(event) => setConfirmTestRecipient(event.target.checked)} />
                          I authorize one test message to this exact address.
                        </label>
                        <div className="form-actions">
                          <button className="btn-secondary" type="button" disabled={testSubmitting} onClick={() => { setTestSender(null); setTestOperation(null); }}>
                            Cancel
                          </button>
                          <button className="btn-primary" disabled={testSender.canStartControlledTest !== true || testSubmitting || !!testOperation || !testRecipient.trim() || !confirmTestRecipient}>
                            {testSubmitting ? 'Queueing...' : testOperation ? 'Test submitted' : testSender.testDeliveryMode === 'LocalCapture' ? 'Capture local test' : 'Send test message'}
                          </button>
                        </div>
                      </form>
                    )}
                    {testOperation && testSender?.mailboxRegistrationId === mailbox.mailboxRegistrationId && (
                      <div className={`sender-test-operation${TEST_FAILURE_STATES.has(testOperation.state) ? ' sender-test-operation--failed' : ''}`}
                        role={TEST_FAILURE_STATES.has(testOperation.state) ? 'alert' : 'status'}>
                        <strong>Test {testOperation.state}</strong>
                        <p>{testOutcomeMessage(testOperation)}</p>
                        <div>Operation: {testOperation.operationId}</div>
                        <div>Updated: {testOperation.updatedAt ? formatDateTime(testOperation.updatedAt) : 'Not recorded'}</div>
                        {testOperation.providerStatusCode && <span> · HTTP {testOperation.providerStatusCode}</span>}
                        {testOperation.providerCorrelationId && <span> · Correlation {testOperation.providerCorrelationId}</span>}
                        {testOperation.errorCode && <span> · Code: {testOperation.errorCode}</span>}
                        {!TEST_TERMINAL_STATES.has(testOperation.state) && (
                          <button className="btn-secondary" type="button" onClick={() => void refreshTestOperation()}>Refresh status</button>
                        )}
                      </div>
                    )}
                    {aliasSender?.mailboxRegistrationId === mailbox.mailboxRegistrationId && (
                      <form className="exchange-sender-form evidence-form" onSubmit={(event) => void confirmAlias(event)}>
                        <h4>Confirm received alias: {aliasSender.label}</h4>
                        {aliasLoading ? <div className="loading">Loading accepted test...</div> : aliasOperation && (
                          <>
                            <p>Accepted test operation {aliasOperation.operationId}. Inspect the received message headers and reply behavior.</p>
                            <label>Observed From address
                              <input type="email" value={aliasFrom} onChange={(event) => setAliasFrom(event.target.value)} required />
                            </label>
                            <label>Observed Sender address
                              <input type="email" value={aliasSenderAddress} onChange={(event) => setAliasSenderAddress(event.target.value)} required />
                            </label>
                            <label>Observed reply-to address
                              <input type="email" value={aliasReplyTo} onChange={(event) => setAliasReplyTo(event.target.value)} required />
                            </label>
                            <label className="evidence-check">
                              <input type="checkbox" checked={confirmReceivedMessage} onChange={(event) => setConfirmReceivedMessage(event.target.checked)} />
                              I inspected the message received at the confirmed test recipient.
                            </label>
                            <label className="evidence-check">
                              <input type="checkbox" checked={confirmNoOnBehalfOf} onChange={(event) => setConfirmNoOnBehalfOf(event.target.checked)} />
                              No unintended “on behalf of” presentation was present.
                            </label>
                            <div className="form-actions">
                              <button className="btn-secondary" type="button" disabled={saving} onClick={() => setAliasSender(null)}>Cancel</button>
                              <button className="btn-primary" disabled={saving || !aliasFrom.trim() || !aliasSenderAddress.trim() ||
                                !aliasReplyTo.trim() || !confirmReceivedMessage || !confirmNoOnBehalfOf}>
                                {saving ? 'Recording...' : 'Confirm alias behavior'}
                              </button>
                            </div>
                          </>
                        )}
                      </form>
                    )}
                    {loadingSenders ? <div className="loading">Loading send addresses...</div> : (
                      <table className="data-table exchange-senders-table">
                        <thead><tr><th>Address</th><th>Eligibility</th><th>Status</th><th>Actions</th></tr></thead>
                        <tbody>
                          {senders.map((sender) => (
                            <tr key={sender.senderIdentityId}>
                              <td><strong>{sender.label}</strong><div>{sender.fromAddress}</div></td>
                              <td>
                                <span className={`status-badge ${sender.canSend ? 'enabled' : 'disabled'}`}>
                                  {sender.canSend ? 'Eligible' : ELIGIBILITY_LABELS[sender.eligibilityCode] || 'Evidence required'}
                                </span>
                                {sender.eligibilityCode === 'EXACT_ALIAS_CONFIRMATION_REQUIRED' && !sender.controlledSendAccepted &&
                                  sender.canStartControlledTest !== true && (
                                    <div className="help-text" role="status">{sender.testUnavailableReason || 'Test availability has not been confirmed for this environment.'}</div>
                                  )}
                              </td>
                              <td>{sender.isRetired ? 'Retired' : sender.isEnabled ? 'Enabled' : 'Disabled'}</td>
                              <td>
                                <div className="exchange-sender-actions">
                                  {!sender.isRetired && <button className="btn-secondary" type="button" disabled={saving}
                                    aria-label={`Edit send address ${sender.label}`} title="Edit send address"
                                    onClick={() => { setEditingSender(sender); setEditingMailbox(null); setEditName(sender.label); setEditDisplayName(sender.displayName || ''); setError(null); setNotice(null); }}>
                                    <Pencil size={16} aria-hidden="true" />
                                  </button>}
                                  {!sender.isRetired && <button className="btn-secondary" type="button" disabled={saving} onClick={() => void toggleSender(sender)}>
                                    {sender.isEnabled ? 'Disable' : 'Enable'}
                                  </button>}
                                  {!sender.isRetired && sender.isEnabled && mailbox.isEnabled && !sender.canSend &&
                                    !sender.controlledSendAccepted && sender.eligibilityCode !== 'EXACT_ALIAS_CONFIRMATION_REQUIRED' && (
                                    <button className="btn-secondary" type="button" disabled={saving}
                                      onClick={() => { setEvidenceSender(sender); setEvidenceChecks({ sharedMailbox: false, addressMembership: false, applicationSend: false, aliasPrerequisites: false }); }}>
                                      Record prerequisites
                                    </button>
                                  )}
                                  {!sender.isRetired && sender.isEnabled && mailbox.isEnabled && !sender.canSend &&
                                    sender.eligibilityCode === 'EXACT_ALIAS_CONFIRMATION_REQUIRED' && sender.controlledSendAccepted !== true && (
                                      <button className="btn-secondary" type="button" disabled={saving || testSubmitting || sender.canStartControlledTest !== true}
                                        title={sender.testUnavailableReason || undefined}
                                        onClick={() => {
                                          setTestSender(sender);
                                          setTestRecipient('');
                                          setConfirmTestRecipient(false);
                                          setTestIdempotencyKey(crypto.randomUUID());
                                          setTestOperation(null);
                                        }}>
                                        Start controlled test
                                      </button>
                                    )}
                                  {!sender.isRetired && sender.isEnabled && mailbox.isEnabled && sender.controlledSendAccepted &&
                                    !sender.receivedAliasConfirmed && (
                                      <button className="btn-secondary" type="button" disabled={saving || aliasLoading}
                                        onClick={() => void openAliasConfirmation(sender)}>Confirm received alias</button>
                                    )}
                                  {!sender.isRetired && <button className="btn-danger" type="button" disabled={saving} onClick={() => void retireSender(sender)}>Retire</button>}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {!senders.length && !loadingSenders && <tr><td colSpan={4} className="empty-row">No send addresses registered for this mailbox.</td></tr>}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </article>
            );
          })}
          {!mailboxes.length && <div className="empty-row">No mailboxes registered.</div>}
        </div>
      )}
    </section>
  );
};
